import React, { useEffect, useRef, useState } from "react";
import {
  formatOfferCountdown,
  getOfferRemainingSeconds,
} from "../../utils/offerTime";

export default function OfferCountdown({
  endTime = null,
  toDate = null,
  remainingSeconds = null,
  loadedAt = null,
  initialNow = null,
  fallbackText = "Limited time offer",
  expiredText = "Offer expired",
  showZeroDays = false,
  className = "",
  onExpire = null,
}) {
  const resolvedEndTime = endTime ?? toDate ?? null;
  const [resolvedRemainingSeconds, setResolvedRemainingSeconds] = useState(() =>
    getOfferRemainingSeconds({
      endTime: resolvedEndTime,
      toDate: resolvedEndTime,
      remainingSeconds,
      loadedAt,
      now: initialNow ?? Date.now(),
    }),
  );
  const intervalIdRef = useRef(null);
  const hasTriggeredExpireRef = useRef(false);

  useEffect(() => {
    const clearExistingInterval = () => {
      if (intervalIdRef.current !== null && typeof window !== "undefined") {
        window.clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };

    const updateCountdown = () => {
      const nextRemainingSeconds = getOfferRemainingSeconds({
        endTime: resolvedEndTime,
        toDate: resolvedEndTime,
        remainingSeconds,
        loadedAt,
        now: Date.now(),
      });

      setResolvedRemainingSeconds((currentValue) =>
        currentValue === nextRemainingSeconds ? currentValue : nextRemainingSeconds,
      );

      if (
        nextRemainingSeconds !== null &&
        nextRemainingSeconds <= 0
      ) {
        clearExistingInterval();
      }

      return nextRemainingSeconds;
    };

    clearExistingInterval();

    const initialRemainingSeconds = updateCountdown();

    if (
      typeof window === "undefined" ||
      initialRemainingSeconds === null ||
      initialRemainingSeconds <= 0
    ) {
      return clearExistingInterval;
    }

    intervalIdRef.current = window.setInterval(updateCountdown, 1000);

    return clearExistingInterval;
  }, [loadedAt, remainingSeconds, resolvedEndTime]);

  const isExpired =
    resolvedRemainingSeconds !== null && resolvedRemainingSeconds <= 0;

  useEffect(() => {
    if (!isExpired || typeof onExpire !== "function") {
      hasTriggeredExpireRef.current = false;
      return;
    }

    if (hasTriggeredExpireRef.current) {
      return;
    }

    hasTriggeredExpireRef.current = true;
    onExpire();
  }, [isExpired, onExpire]);

  const text =
    resolvedRemainingSeconds === null
      ? fallbackText
      : isExpired
        ? expiredText
        : formatOfferCountdown(resolvedRemainingSeconds, {
            alwaysShowDays: showZeroDays,
          });

  if (!text) {
    return null;
  }

  return <span className={className}>{text}</span>;
}
